/**
 * Account Hooks
 * React Query hooks for user profile management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accountService, type UpdateProfileInput, type ChangePasswordInput } from '../services/account.service';
import { settingsService } from '../services/settings.service';

export const accountKeys = {
  all: ['account'] as const,
  profile: () => [...accountKeys.all, 'profile'] as const,
  settings: () => [...accountKeys.all, 'settings'] as const,
  profilePicture: (userId: string) => [...accountKeys.all, 'profilePicture', userId] as const,
  avatarColor: () => [...accountKeys.all, 'avatarColor'] as const,
};

export function useMyProfile() {
  return useQuery({
    queryKey: accountKeys.profile(),
    queryFn: () => accountService.getMyProfile(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAccountSettings() {
  return useQuery({
    queryKey: accountKeys.settings(),
    queryFn: () => accountService.getAccountSettings(),
    staleTime: 30 * 60 * 1000,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProfileInput) => accountService.updateMyProfile(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.profile() });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) => accountService.changePassword(input),
  });
}

export function useProfilePicture(userId: string) {
  return useQuery({
    queryKey: accountKeys.profilePicture(userId),
    queryFn: () => accountService.getProfilePicture(userId),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useUploadProfilePicture() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ type, file }: { type: number; file?: File }) =>
      accountService.uploadProfilePicture(type, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
    },
  });
}

/**
 * Get the current user's avatar color setting
 * Returns null if not set (should use default brandPrimary)
 */
export function useMyAvatarColor() {
  return useQuery({
    queryKey: accountKeys.avatarColor(),
    queryFn: () => settingsService.getAvatarColor(),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Mutation to save avatar color for current user
 */
export function useSetAvatarColor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (color: string) => settingsService.setAvatarColor(color),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.avatarColor() });
    },
  });
}
